<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯角色' : '新增角色' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-arrow-left-line"></i> 返回
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">處理中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-row">
          <div class="form-group">
            <label for="roleCode" class="required">角色代碼</label>
            <input
              id="roleCode"
              v-model="form.roleCode"
              type="text"
              class="form-control"
              :disabled="isEdit"
              placeholder="例如：system_admin"
              required
            />
            <small class="form-text">唯一識別碼，僅支援英數字和底線，建立後無法修改</small>
          </div>

          <div class="form-group">
            <label for="roleName" class="required">角色名稱</label>
            <input
              id="roleName"
              v-model="form.roleName"
              type="text"
              class="form-control"
              placeholder="例如：系統管理員"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="accountType" class="required">適用帳號類型</label>
            <select
              id="accountType"
              v-model="form.accountType"
              class="form-control"
              required
            >
              <option value="">請選擇</option>
              <option value="system">系統帳號</option>
              <option value="platform">平台帳號</option>
            </select>
          </div>

          <div class="form-group">
            <label for="status" class="required">狀態</label>
            <select
              id="status"
              v-model="form.status"
              class="form-control"
              required
            >
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
            <p class="form-hint">
              <strong>⚠️ 停用角色的影響：</strong><br>
              • 無法在帳號建立/編輯時被指定<br>
              • 不會出現在帳號建立頁面的角色下拉選單中<br>
              • <strong>已使用該角色的帳號不受影響，仍可正常使用</strong>
            </p>
          </div>
        </div>

        <div class="form-group">
          <label for="description">角色說明</label>
          <textarea
            id="description"
            v-model="form.description"
            class="form-control"
            rows="3"
            placeholder="描述角色的職責和權限範圍..."
          />
        </div>

        <div class="form-group">
          <label for="sortOrder">排序順序</label>
          <input
            id="sortOrder"
            v-model.number="form.sortOrder"
            type="number"
            class="form-control"
            min="0"
            placeholder="0"
          />
          <small class="form-text">數字越小越靠前，預設為 0</small>
        </div>

        <!-- 模組設定區塊 -->
        <ModuleSelector
          v-model="selectedModuleIds"
          title="模組設定"
          description="選擇此角色可使用的功能模組。使用此角色的帳號只能看到已勾選的模組及其程式。"
          :modules="filteredModules"
          :loading="loadingModules"
          :error="modulesError"
          :disabledModuleIds="disabledModuleIds"
          @reload="fetchModules"
        />

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { rolesService } from '@/api/modules/roles.js';
import { moduleService } from '@/api/modules/modules.js';
import { showSuccess, showError } from '@/utils/swal.js';
import ModuleSelector from '@/components/ModuleSelector.vue';
import { moduleMatchesAccountType, filterSelectedModuleIdsForAccountType } from '@/utils/moduleVisibility.js';

export default {
  name: 'RoleCreate',
  components: {
    ModuleSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params.roleCode);
    const submitting = ref(false);

    // 模組相關狀態
    const loadingModules = ref(false);
    const modulesError = ref('');
    const availableModules = ref([]);
    const selectedModuleIds = ref([]);

    // 系統設定模組 ID（系統管理員必須擁有）
    const SYSTEM_SETTINGS_MODULE_CODE = 'SYSTEM_SETTINGS';
    
    // 計算禁用的模組 ID
    const disabledModuleIds = computed(() => {
      if (form.value.roleCode === 'system_admin') {
        // 找到系統設定模組的 ID
        const systemSettingsModule = availableModules.value.find(
          m => m.moduleCode === SYSTEM_SETTINGS_MODULE_CODE || m.module_code === SYSTEM_SETTINGS_MODULE_CODE
        );
        return systemSettingsModule ? [systemSettingsModule.id] : [];
      }
      return [];
    });

    // 根據帳號類型過濾模組
    const filteredModules = computed(() => {
      if (!form.value.accountType) {
        return availableModules.value;
      }
      return availableModules.value.filter(
        m => moduleMatchesAccountType(m, form.value.accountType)
      );
    });

    const form = ref({
      roleCode: '',
      roleName: '',
      accountType: '',
      description: '',
      status: 'active',
      sortOrder: 0
    });


    // 載入所有可用模組
    const fetchModules = async () => {
      loadingModules.value = true;
      modulesError.value = '';

      try {
        const response = await moduleService.getModules({
          status: 'active',
          all: true
        });

        if (response && response.success && response.data) {
          availableModules.value = response.data.items || [];
        } else {
          modulesError.value = '無法載入模組列表';
        }
      } catch (error) {
        console.error('載入模組列表失敗:', error);
        modulesError.value = '載入模組列表失敗，請稍後再試';
      } finally {
        loadingModules.value = false;
      }
    };

    // 載入角色已關聯的模組
    const fetchRoleModules = async (roleCode) => {
      try {
        const response = await rolesService.getRoleModules(roleCode);

        if (response && response.success && response.data) {
          selectedModuleIds.value = (response.data || []).map(m => m.id);
          console.log('✅ 載入角色模組成功，已選擇:', selectedModuleIds.value.length, '個模組');
          
          // 如果是系統管理員，確保系統設定模組被選中
          if (roleCode === 'system_admin') {
            const systemSettingsModule = availableModules.value.find(
              m => m.moduleCode === SYSTEM_SETTINGS_MODULE_CODE || m.module_code === SYSTEM_SETTINGS_MODULE_CODE
            );
            if (systemSettingsModule && !selectedModuleIds.value.includes(systemSettingsModule.id)) {
              selectedModuleIds.value.push(systemSettingsModule.id);
            }
          }
        }
      } catch (error) {
        console.error('載入角色模組失敗:', error);
        // 不顯示錯誤，允許用戶繼續編輯
      }
    };

    // 儲存角色模組關聯
    const saveRoleModules = async (roleCode) => {
      try {
        const moduleIds = filterSelectedModuleIdsForAccountType(
          availableModules.value,
          selectedModuleIds.value,
          form.value.accountType
        );
        await rolesService.setRoleModules(roleCode, moduleIds);
        return true;
      } catch (error) {
        console.error('儲存角色模組失敗:', error);
        throw error;
      }
    };

    const fetchRole = async () => {
      if (!isEdit.value) return;

      try {
        console.log('[RoleEdit] Fetching role:', route.params.roleCode);
        const response = await rolesService.getRole(route.params.roleCode);
        console.log('[RoleEdit] API response:', response);
        
        if (response && response.success && response.data) {
          const role = response.data;
          console.log('[RoleEdit] Role data:', role);
          form.value = {
            roleCode: role.role_code,
            roleName: role.role_name,
            accountType: role.account_type,
            description: role.description || '',
            status: role.status,
            sortOrder: role.sort_order || 0
          };
          console.log('[RoleEdit] Form updated:', form.value);

          // 載入模組
          await fetchModules();
          await fetchRoleModules(route.params.roleCode);
        } else {
          console.warn('[RoleEdit] Invalid response format:', response);
        }
      } catch (err) {
        console.error('[RoleEdit] 獲取角色詳情失敗:', err);
        console.error('[RoleEdit] Error response:', err.response);
        await showError('錯誤', err.response?.data?.message || '獲取角色詳情失敗');
        router.push('/system/roles');
      }
    };

    const handleSubmit = async () => {
      submitting.value = true;

      try {
        const roleData = {
          roleCode: form.value.roleCode,
          roleName: form.value.roleName,
          accountType: form.value.accountType,
          description: form.value.description || null,
          status: form.value.status,
          sortOrder: form.value.sortOrder || 0
        };

        if (isEdit.value) {
          // 更新時不需要 roleCode
          delete roleData.roleCode;
          await rolesService.updateRole(route.params.roleCode, roleData);
          
          // 確保必選模組在列表中（系統管理員必須有系統設定模組）
          if (form.value.roleCode === 'system_admin') {
            const systemSettingsModule = availableModules.value.find(
              m => m.moduleCode === SYSTEM_SETTINGS_MODULE_CODE || m.module_code === SYSTEM_SETTINGS_MODULE_CODE
            );
            if (systemSettingsModule && !selectedModuleIds.value.includes(systemSettingsModule.id)) {
              selectedModuleIds.value.push(systemSettingsModule.id);
            }
          }
          
          // 更新模組
          await saveRoleModules(route.params.roleCode);
          
          await showSuccess('成功', '角色更新成功');
        } else {
          const response = await rolesService.createRole(roleData);
          
          // 新增模式：同時儲存模組關聯
          if (response && response.success) {
            // 確保必選模組在列表中（系統管理員必須有系統設定模組）
            if (form.value.roleCode === 'system_admin') {
              const systemSettingsModule = availableModules.value.find(
                m => m.moduleCode === SYSTEM_SETTINGS_MODULE_CODE || m.module_code === SYSTEM_SETTINGS_MODULE_CODE
              );
              if (systemSettingsModule && !selectedModuleIds.value.includes(systemSettingsModule.id)) {
                selectedModuleIds.value.push(systemSettingsModule.id);
              }
            }
            
            try {
              await saveRoleModules(form.value.roleCode);
            } catch (moduleError) {
              console.error('儲存模組關聯失敗:', moduleError);
              await showError('警告', '角色建立成功，但模組關聯儲存失敗。請稍後重試。');
              router.push('/system/roles');
              return;
            }
          }
          
          await showSuccess('成功', '角色建立成功');
        }

        router.push('/system/roles');
      } catch (err) {
        console.error('儲存角色失敗:', err);
        await showError('錯誤', err.response?.data?.message || '儲存角色失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/system/roles');
    };

    // 監聽模組列表和角色代碼，確保系統管理員始終有系統設定模組
    watch(
      [() => availableModules.value.length, () => form.value.roleCode, () => selectedModuleIds.value],
      () => {
        if (form.value.roleCode === 'system_admin' && availableModules.value.length > 0) {
          const systemSettingsModule = availableModules.value.find(
            m => m.moduleCode === SYSTEM_SETTINGS_MODULE_CODE || m.module_code === SYSTEM_SETTINGS_MODULE_CODE
          );
          if (systemSettingsModule && !selectedModuleIds.value.includes(systemSettingsModule.id)) {
            selectedModuleIds.value.push(systemSettingsModule.id);
          }
        }
      },
      { immediate: true }
    );

    // 監聽帳號類型變化，當切換為平台帳號時，清除系統模組的選擇
    watch(
      () => form.value.accountType,
      (newAccountType) => {
        if (newAccountType === 'platform' && availableModules.value.length > 0) {
          // 找出所有系統類型的模組 ID
          const systemModuleIds = availableModules.value
            .filter(m => m.moduleCategory === 'system' || m.module_category === 'system')
            .map(m => m.id);
          
          // 從已選擇的模組中移除系統模組
          selectedModuleIds.value = selectedModuleIds.value.filter(
            id => !systemModuleIds.includes(id)
          );
        }
      }
    );

    onMounted(() => {
      if (isEdit.value) {
        fetchRole();
      } else {
        // 新增模式下也載入模組列表
        fetchModules();
      }
    });

    return {
      isEdit,
      form,
      submitting,
      loadingModules,
      modulesError,
      availableModules,
      selectedModuleIds,
      disabledModuleIds,
      filteredModules,
      fetchModules,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-text {
  display: block;
  margin-top: 5px;
  font-size: 12px;
  color: #666;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
}

/* 模組設定區塊 */
.form-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px solid #e9ecef;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
